import { useMutation, useQueryClient } from 'react-query'
import { BASE_API_URL } from 'constants/env'
import { API_METHODS } from 'constants/api-methods'
import { apiHeaders } from 'utils/api-headers'
import { store } from 'index'
import { handleErrors } from 'utils/handle-errors'
import toast from 'react-hot-toast'

interface Device {
  id: string
  name: string
  type: 'mac-mini' | 'physical-phone'
}

interface AssignDevicesParams {
  devices: Device[]
  ansibleId: string
  terminalId: string
}

interface AssignmentResult {
  deviceId: string
  deviceName: string
  success: boolean
  error?: string
}

interface AssignmentResponse {
  results: AssignmentResult[]
  successCount: number
  failureCount: number
  totalCount: number
}

const updateSingleDevice = async (
  device: Device,
  ansibleId: string
): Promise<AssignmentResult> => {
  try {
    const endpoint =
      device.type === 'mac-mini'
        ? `${BASE_API_URL}/api/v4/phone_number_service/mac_minis/${device.id}`
        : `${BASE_API_URL}/api/v4/phone_number_service/physical_phones/${device.id}`

    let response = await fetch(endpoint, {
      method: API_METHODS.PUT,
      headers: apiHeaders(store.getState()),
      body: JSON.stringify({
        ansibleId,
        status: device.type === 'mac-mini' ? 'PROVISIONING' : 'AVAILABLE',
      }),
    })
    response = handleErrors(response)

    if (!response.ok) {
      const errorData = await response
        .json()
        .catch(() => ({ message: response.statusText }))
      throw new Error(errorData.message || response.statusText)
    }

    return {
      deviceId: device.id,
      deviceName: device.name,
      success: true,
    }
  } catch (error) {
    return {
      deviceId: device.id,
      deviceName: device.name,
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error',
    }
  }
}

const assignDevicesToAnsible = async ({
  devices,
  ansibleId,
  terminalId,
}: AssignDevicesParams): Promise<AssignmentResponse> => {
  // Process devices with Promise.allSettled to handle parallel execution
  const devicePromises = devices.map(
    (device, index) =>
      new Promise<AssignmentResult>((resolve) => {
        // Add small delay for subsequent requests to be gentle on the API
        const delay = index === 0 ? 0 : index * 100
        setTimeout(async () => {
          const result = await updateSingleDevice(device, ansibleId)
          resolve(result)
        }, delay)
      })
  )

  const results = await Promise.all(devicePromises)
  const successCount = results.filter((r) => r.success).length
  const failureCount = results.filter((r) => !r.success).length

  return {
    results,
    successCount,
    failureCount,
    totalCount: devices.length,
  }
}

export const useBulkDeviceAssignment = () => {
  const queryClient = useQueryClient()

  return useMutation(assignDevicesToAnsible, {
    onError: (error: Error) => {
      console.error('Device assignment failed:', error)
      toast.error(`Device assignment failed: ${error.message}`)
    },
    onSuccess: (data, variables) => {
      const { successCount, failureCount, totalCount } = data

      if (failureCount === 0) {
        toast.success(
          `Successfully assigned ${successCount} device${
            successCount === 1 ? '' : 's'
          } to ansible`
        )
      } else if (successCount > 0) {
        toast.success(
          `Assigned ${successCount}/${totalCount} devices. ${failureCount} failed.`
        )
      } else {
        toast.error(`Failed to assign any devices`)
      }

      // Invalidate relevant queries
      queryClient.invalidateQueries(['received-mac-minis'])
      queryClient.invalidateQueries(['received-physical-phones'])
      queryClient.invalidateQueries(['received-mac-minis-count'])
      queryClient.invalidateQueries(['received-physical-phones-count'])
      queryClient.invalidateQueries(['mac-minis-by-ansible'])
      queryClient.invalidateQueries(['physical-phones-by-ansible'])
      queryClient.invalidateQueries(['ansibles'])
      queryClient.invalidateQueries(['ansible-detail', variables.ansibleId])
      queryClient.invalidateQueries(['terminal-detail', variables.terminalId])
    },
  })
}
